import CryptoJS from 'crypto-es'
import dayjs from 'dayjs'

import { ImportClient, ImportedArticle, ImportedFile } from 'api/clients/ImportClient'
import { WebsocketConsumer } from 'providers/WebsocketProvider'
import {
  FileImport,
  FileImportStatus,
  ImportArticle,
  ImportContainer,
  ImportListing,
  ImportStateType,
  SourceType,
} from './Types'
import { ViewType } from 'types'

export const importStatusFrom = (status: string): FileImportStatus => {
  switch (status) {
    case 'partially_successful':
      return FileImportStatus.PartiallySuccessful
    case 'successful':
      return FileImportStatus.Successful
    case 'failed':
      return FileImportStatus.Failed
    case 'cancelled':
      return FileImportStatus.Cancelled
    default:
      return FileImportStatus.Failed
  }
}

export const title = (source: SourceType, importState: ImportStateType) => {
  if (importState === ImportStateType.Import) {
    switch (source) {
      case SourceType.Main:
        return 'Store all your files'
      case SourceType.Notion:
        return 'Import files exported from Notion'
      case SourceType.Confluence:
        return 'Import files exported from Confluence'
      case SourceType.GoogleDocs:
        return 'Import from Google Docs'
      case SourceType.Airtable:
        return 'Upload via link from Airtable'
      case SourceType.Canva:
        return 'Upload via link from Canva'
      case SourceType.Figma:
        return 'Upload via link from Figma'
      case SourceType.GoogleSheets:
        return 'Upload via link from Google Sheets'
      case SourceType.GoogleSlides:
        return 'Upload via link from Google Slides'
    }
  } else if (importState === ImportStateType.Cancelling) {
    return 'Cancel Import'
  } else if (importState === ImportStateType.Importing) {
    return 'Importing...'
  } else {
    return 'Import Complete!'
  }
}

const readChunked = (
  file: File,
  chunkCallback: (chunkResult: string, offset: number, fileSize: number) => void,
  endCallback: (error?: DOMException | ProgressEvent<FileReader> | null) => void,
) => {
  const fileSize = file.size
  const chunkSize = 4 * 1024 * 1024 // 4MB
  let offset = 0

  const reader = new FileReader()
  reader.onload = function () {
    // Read as binary string, final boolean will always be false, satisfying TS
    if (reader.error || !reader.result || reader.result instanceof ArrayBuffer) {
      endCallback(reader.error || new DOMException('No result'))
      return
    }
    offset += reader.result.length
    chunkCallback(reader.result, offset, fileSize)
    if (offset >= fileSize) {
      endCallback(null)
      return
    }
    readNext()
  }

  reader.onerror = function (err) {
    endCallback(err)
  }

  function readNext() {
    const fileSlice = file.slice(offset, offset + chunkSize)
    reader.readAsBinaryString(fileSlice)
  }
  readNext()
}

export const md5FromFile = (file: File, progressCallback?: (progress: number) => void) => {
  return new Promise<string>((resolve, reject) => {
    let md5 = CryptoJS.algo.MD5.create()
    readChunked(
      file,
      (chunk, offset, total) => {
        md5.update(CryptoJS.enc.Latin1.parse(chunk))
        if (progressCallback) {
          progressCallback(offset / total)
        }
      },
      (err) => {
        if (err) {
          reject(err)
        } else {
          const hash = md5.finalize()
          const hashBase64 = hash.toString(CryptoJS.enc.Base64)
          resolve(hashBase64)
        }
      },
    )
  })
}

export const convertBytesToFile = (binaryString: string, fileName: string) => {
  const bl = binaryString.length
  const buffer = new ArrayBuffer(bl)
  let chars = new Uint8Array(buffer)
  for (let i = 0; i < bl; i++) chars[i] = binaryString.charCodeAt(i)
  const newFile = new File([buffer], fileName, {
    type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  })

  return newFile
}

export const defaultQueryString = 'mimeType = "application/vnd.google-apps.document" and trashed = false'

export const uploadFile = (
  fileImport: FileImport,
  company_id: number,
  rootFolderId: string | null,
): Promise<number> => {
  return new Promise<number>(async (resolve, reject) => {
    const file = fileImport.originalFile
    let checksum: string
    try {
      checksum = await md5FromFile(file)
    } catch (err) {
      return reject('Failed to create md5 checksum for the file')
    }
    const body = {
      file: {
        filename: file.name,
        content_type: file.type ? file.type : file.name.endsWith('.md') && 'text/markdown',
        byte_size: file.size,
        metadata: { identified: true },
        checksum,
      },
    }
    const abortSignal = fileImport.abortController.signal
    const { data, type } = await ImportClient.getSignedUrl(body, abortSignal)
    if (type !== 'success') return reject('Failed to create a signed url for upload')
    try {
      const resp = await ImportClient.uploadTos3(
        file,
        data.url.direct_upload.url,
        data.url.direct_upload.headers,
        abortSignal,
      )
      if (resp.status < 200 || resp.status > 299) return reject('Failed to upload the file')
    } catch (err) {
      return reject('Failed to upload the file')
    }

    const reportBody = {
      import: {
        file: data.url.blob_signed_id,
        workspace_id: company_id,
        source: fileImport.source,
        schema_version: 3,
        filename: fileImport.originalFile.name,
        root_folder_id: rootFolderId,
      },
    }
    const uploadCompleteResp = await ImportClient.reportUploadComplete(reportBody, company_id, abortSignal)
    if (uploadCompleteResp.type === 'success') {
      return resolve(uploadCompleteResp.data.import_id)
    } else {
      return reject('Failed to report upload completion')
    }
  })
}

export const listenProcessing = (socket: WebsocketConsumer, import_id: number): Promise<ImportedFile> => {
  return new Promise<ImportedFile>(async (resolve, reject) => {
    if (!socket) return reject('Socket connection failure')
    const subscription = socket.subscriptions.create(
      { channel: 'ImportChannel', import_id },
      {
        received(importMsg: { import: ImportedFile }) {
          if (importMsg.import.status !== 'processing') {
            subscription.unsubscribe()
            return resolve(importMsg.import)
          }
        },
      },
    )
  })
}

export const convertToArticles = (rawList: ImportedFile[]): ImportArticle[] => {
  const convert = (item: ImportedFile, article: ImportedArticle): ImportArticle => {
    return {
      import_id: item.id,
      filename: item.filename,
      status: item.status,
      created_at: item.created_at,
      accessor_info: article.accessor_info,
      accessor_names: article.accessor_names,
      ancestor_projects: [],
      id: article.key,
      owner: '',
      title: article.title,
      type: 'article',
      updated_at: item.created_at,
      location: article.location,
      workspace_id: '',
      real_workspace_id: 0,
      key: article.key,
      version_control_mode: {
        value: false,
        metadata: null,
      },
      is_root: false,
      view_metadata: {
        view: ViewType.List,
      },
    }
  }
  const FAILED_ARTICLE = {
    id: -1,
    key: '',
    title: '',
    status: 'failed',
    accessor_info: '',
    accessor_names: [],
    location: { key: '', name: '' },
  }

  return rawList
    .map((item) => {
      // TODO: Fix this the next time the file is edited.
      // eslint-disable-next-line no-nested-ternary
      const articles: ImportedArticle[] = item.articles.length
        ? item.articles
        : item.status === 'successful'
        ? []
        : [FAILED_ARTICLE]

      return articles
        .filter((article) => article.accessor_info !== null) // filter out deleted
        .map((article) => convert(item, article))
    })
    .reduce((a, b) => [...a, ...b], [])
}

export const sortImportedFileList = (rawList: ImportArticle[]): ImportListing[] => {
  let containerList: ImportContainer[] = []
  for (const item of rawList) {
    let existing = containerList.find((r) => r.id === item.import_id)
    if (existing) existing.articles.push(item)
    else
      containerList.push({
        id: item.import_id,
        filename: item.filename,
        status: item.status,
        created_at: item.created_at,
        articles: [item],
      })
  }

  let now: ImportContainer[] = [],
    today: ImportContainer[] = [],
    yesterday: ImportContainer[] = [],
    earlierThisWeek: ImportContainer[] = [],
    lastWeek: ImportContainer[] = [],
    earlierThisMonth: ImportContainer[] = [],
    lastMonth: ImportContainer[] = [],
    older: ImportContainer[] = []

  for (const item of containerList) {
    if (dayjs(item.created_at).isSame(dayjs(), 'hour')) {
      now.push(item)
    } else if (dayjs(item.created_at).isSame(dayjs(), 'day')) {
      today.push(item)
    } else if (dayjs(item.created_at).isSame(dayjs().subtract(1, 'day'), 'day')) {
      yesterday.push(item)
    } else if (dayjs(item.created_at).isSame(dayjs(), 'week')) {
      earlierThisWeek.push(item)
    } else if (dayjs(item.created_at).isSame(dayjs().subtract(1, 'week'), 'week')) {
      lastWeek.push(item)
    } else if (dayjs(item.created_at).isSame(dayjs(), 'month')) {
      earlierThisMonth.push(item)
    } else if (dayjs(item.created_at).isSame(dayjs().subtract(1, 'month'), 'month')) {
      lastMonth.push(item)
    } else {
      older.push(item)
    }
  }

  const sorter = (a: ImportContainer, b: ImportContainer): number => {
    return dayjs(b.created_at).diff(dayjs(a.created_at))
  }

  let importLists: ImportListing[] = []
  if (now.length) importLists.push({ title: 'Just Now', containers: now.sort(sorter) })
  if (today.length) importLists.push({ title: 'Earlier Today', containers: today.sort(sorter) })
  if (yesterday.length) importLists.push({ title: 'Yesterday', containers: yesterday.sort(sorter) })
  if (earlierThisWeek.length) importLists.push({ title: 'Earlier This Week', containers: earlierThisWeek.sort(sorter) })
  if (lastWeek.length) importLists.push({ title: 'Last Week', containers: lastWeek.sort(sorter) })
  if (earlierThisMonth.length)
    importLists.push({ title: 'Earlier This Month', containers: earlierThisMonth.sort(sorter) })
  if (lastMonth.length) importLists.push({ title: 'Last Month', containers: lastMonth.sort(sorter) })
  if (older.length) importLists.push({ title: 'Older', containers: older.sort(sorter) })
  return importLists
}

export const getHandbookNameFromImports = (fileImport: FileImport) => {
  const topLevelFolders = fileImport.topLevelFolders
  return topLevelFolders?.length === 1 ? topLevelFolders[0] : ''
}
